import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { ApiConstants } from '../constants/api-constants';
import { PlanEntry, WeeklyPlan, WeeklyPlanSettings } from '../main/weekly-plans/weekly-plans.models';

export type FillStatus = 'filled' | 'incomplete' | 'not_started' | 'suspicious';

export interface FillReportRow {
  teacherId: string;
  teacherName: string;
  teacherUsername: string;
  classId: string;
  className: string;
  subjectId: string;
  subjectName: string;
  status: FillStatus;
  fillPercent: number;
  meaningfulCells: number;
  fillerCells: number;
  emptyCells: number;
  expectedCells: number;
  avgMeaningfulLength: number;
  planId: string | null;
}

export interface FillReport {
  weekOrdinal: number;
  week: { id: string | null; name: string; active: boolean };
  summary: {
    total: number;
    filled: number;
    incomplete: number;
    not_started: number;
    suspicious: number;
  };
  rows: FillReportRow[];
}

@Injectable({
  providedIn: 'root'
})
export class WeeklyPlansService {
  private readonly url = ApiConstants.URL_WEEKLY_PLANS;

  constructor(private http: HttpClient) { }

  async getByClassAndWeek(classId: string, weekOrdinal: number): Promise<WeeklyPlan | null> {
    const response = await firstValueFrom(
      this.http.get<any>(`${this.url}?class=${classId}&weekOrdinal=${weekOrdinal}`)
    );
    return response.data;
  }

  async upsert(body: {
    class: string;
    weekOrdinal: number;
    startDate?: string;
    endDate?: string;
    schoolName?: string;
    departmentName?: string;
    sectionHeadName?: string;
    status?: string;
    entries?: {
      subject: string;
      teacher?: string;
      forcePageBreak?: boolean;
      cells: PlanEntry['cells'];
    }[];
  }): Promise<WeeklyPlan> {
    const response = await firstValueFrom(this.http.put<any>(`${this.url}/upsert`, body));
    return response.data;
  }

  async getSettings(): Promise<WeeklyPlanSettings> {
    const response = await firstValueFrom(this.http.get<any>(`${this.url}/settings`));
    return response.data;
  }

  async updateSettings(body: WeeklyPlanSettings): Promise<WeeklyPlanSettings> {
    const response = await firstValueFrom(this.http.put<any>(`${this.url}/settings`, body));
    return response.data;
  }

  async getFillReport(weekOrdinal: number): Promise<FillReport> {
    const response = await firstValueFrom(
      this.http.get<any>(`${this.url}/fill-report?weekOrdinal=${weekOrdinal}`)
    );
    return response.data;
  }
}
